var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import React, { Component, Fragment } from "react";
import { Accordion, AccordionDetails, AccordionSummary, Box, Typography } from "@mui/material";
import { NotFoundSvg } from "./NotFoundSvg";
import ErrorStackParser from "error-stack-parser";
import { ExpandMore } from "@mui/icons-material";
const codeSx = {
    background: '#263238',
    color: '#fff',
    fontSize: '0.8rem',
    padding: 1,
    borderRadius: '3px',
    overflowX: 'auto',
    width: '100%'
};
export class ErrorBoundary extends Component {
    constructor(props) {
        super(props);
        this.state = {
            hasError: false
        };
    }
    /**
     * This lifecycle is invoked after an error has been thrown by a descendant component.
     * It receives the error that was thrown as a parameter and should return a value to update state.
     * @param error
     * @doc https://reactjs.org/docs/react-component.html#static-getderivedstatefromerror
     */
    static getDerivedStateFromError(error) {
        // Store error context
        ErrorBoundary.errorContext = {
            url: location.href,
            referrer: document.referrer,
            userAgent: navigator.userAgent,
            error
        };
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }
    componentDidCatch(error, errorInfo) {
        console.error('An error occurred', { error, errorInfo });
    }
    reportBugsnag() {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.props.bugsnagConfig) {
                console.warn('Bugsnag not configured. Skip reporting to bugsnag.');
                return;
            }
            if (!ErrorBoundary.errorContext) {
                console.warn('Error context not specified. Cannot report to bugsnag.');
                return;
            }
            const { bugsnagConfig: { apiKey, getUser } } = this.props;
            const { error, url, referrer, userAgent } = ErrorBoundary.errorContext;
            const stackTraces = ErrorStackParser.parse(error)
                .map(({ fileName: file, lineNumber, columnNumber, functionName: method }) => ({
                file,
                lineNumber,
                columnNumber,
                method
            }));
            const payload = {
                apiKey,
                payloadVersion: '5',
                notifier: {
                    name: "variocube app-ui",
                    version: '1.0.11',
                    url: 'https://github.com'
                },
                events: [
                    {
                        exceptions: [{
                                errorClass: error.message,
                                stacktrace: stackTraces,
                                type: 'browserjs'
                            }],
                        request: {
                            httpMethod: '',
                            url,
                            referrer
                        },
                        user: getUser(),
                        metaData: {
                            userAgent: { userAgent },
                            sessionStorage: sessionStorage,
                            localStorage: localStorage
                        }
                    }
                ]
            };
            try {
                const response = yield fetch('https://notify.bugsnag.com/', {
                    method: 'POST',
                    body: JSON.stringify(payload)
                });
                if (response.ok) {
                    console.info('successfully reported error to bugsnag');
                }
            }
            catch (error) {
                console.log('failed to report error to bugsnag', error);
            }
        });
    }
    renderStorage(storage) {
        const { anonymizeStorageKeyPatterns = [] } = this.props;
        const kv = [];
        for (let i = 0; i < storage.length; i++) {
            const key = storage.key(i);
            if (key) {
                let value = storage.getItem(key);
                if (value && anonymizeStorageKeyPatterns.find(p => key.indexOf(p) > -1)) {
                    value = "*****";
                }
                kv.push({ key, value });
            }
        }
        if (kv.length === 0)
            return React.createElement(Box, { component: "code", sx: Object.assign(Object.assign({}, codeSx), { mb: 2 }) }, "NONE");
        return kv
            .sort((x, y) => x.key.localeCompare(y.key))
            .map((o, i) => {
            let value = o.value;
            try {
                value = JSON.stringify(JSON.parse(value), null, 3);
            }
            catch (error) {
                console.log('cannot parse value', error);
            }
            return (React.createElement(Fragment, { key: 'kv-' + i },
                React.createElement(Typography, { variant: "caption" }, o.key),
                React.createElement(Box, { component: "code", sx: Object.assign(Object.assign({}, codeSx), { mb: 2 }) }, value)));
        });
    }
    render() {
        if (!this.state.hasError)
            return this.props.children;
        this.reportBugsnag()
            .catch(err => console.error('failed to report to bugsnag', err));
        const { errorContext: { error, url, referrer, userAgent } = {} } = ErrorBoundary;
        return (React.createElement(Box, { p: 3 },
            React.createElement(NotFoundSvg, { style: { width: '100%', maxHeight: 300 } }),
            React.createElement(Box, { p: 4 }),
            React.createElement(Typography, { variant: "h2", align: "center" }, "Oops, something went wrong!"),
            React.createElement(Box, { p: 2 }),
            React.createElement(Accordion, null,
                React.createElement(AccordionSummary, { expandIcon: React.createElement(ExpandMore, null) },
                    React.createElement(Typography, null, error === null || error === void 0 ? void 0 : error.message)),
                React.createElement(AccordionDetails, { sx: {
                        display: 'flex',
                        flexDirection: 'column'
                    } },
                    React.createElement(Typography, { variant: "overline" },
                        React.createElement("strong", null, "URL")),
                    React.createElement(Box, { component: "code", sx: codeSx }, url),
                    React.createElement(Box, { my: 1 }),
                    React.createElement(Typography, { variant: "overline" },
                        React.createElement("strong", null, "Referrer")),
                    React.createElement(Box, { component: "code", sx: codeSx }, referrer),
                    React.createElement(Box, { my: 1 }),
                    React.createElement(Typography, { variant: "overline" },
                        React.createElement("strong", null, "User-Agent")),
                    React.createElement(Box, { component: "code", sx: codeSx }, userAgent),
                    React.createElement(Box, { my: 1 }),
                    React.createElement(Typography, { variant: "overline" },
                        React.createElement("strong", null, "Trace")),
                    React.createElement(Box, { component: "code", sx: codeSx }, error === null || error === void 0 ? void 0 : error.stack),
                    React.createElement(Box, { my: 1 }),
                    React.createElement(Typography, { variant: "overline" },
                        React.createElement("strong", null, "Session Storage")),
                    this.renderStorage(sessionStorage),
                    React.createElement(Box, { my: 1 }),
                    React.createElement(Typography, { variant: "overline" },
                        React.createElement("strong", null, "Local Storage")),
                    this.renderStorage(localStorage)))));
    }
}
//# sourceMappingURL=ErrorBoundary.js.map